import {
  UseMutateAsyncFunction,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { set } from 'lodash/fp'
import { useRouter } from 'next/navigation'
import { useMemo } from 'react'
import { useAddEvaluationToDashboardList } from '~/app/(dashboard)/dashboardQuery'
import { getFragmentData, graphql } from '~/gql'
import {
  ChangeEvaluationStatusMutation,
  DashboardEvaluationsQuery,
  EvaluationVersionClientQuery,
  EvaluationVersionInput,
  WorkspaceDashboardQuery,
} from '~/gql/graphql'
import { gqlClient } from '~/hooks/useGraphQL.server'

const changeEvaluationStatusMutation = graphql(`
  mutation ChangeEvaluationStatus($evaluationId: String!, $newStatus: String!) {
    changeEvaluationStatus(evaluationId: $evaluationId, newStatus: $newStatus)
  }
`)

const TargetStatusKeys = ['status', 'status_code']
export enum StatusValue {
  archived = 'inactive',
  deleted = 'deleted',
  active = 'active',
}

// These maps will track the evaluation ids being mutated in different status categories
export const archiveEvaluationMutationMap = new Map<string, boolean>()
export const deleteEvaluationMutationMap = new Map<string, boolean>()

// This map will track evaluation ids being mutated for createEvaluationVersion
export const createEvaluationVersionMutationMap = new Map<string, boolean>()

function addOrRemoveIdFromChangeStatusMutationMap(
  status: StatusValue,
  evaluationVersionId: string,
  operation: 'add' | 'remove'
) {
  if (status === StatusValue.archived || status === StatusValue.active) {
    operation === 'add'
      ? archiveEvaluationMutationMap.set(evaluationVersionId, true)
      : archiveEvaluationMutationMap.delete(evaluationVersionId)
  } else if (status === StatusValue.deleted) {
    operation === 'add'
      ? deleteEvaluationMutationMap.set(evaluationVersionId, true)
      : deleteEvaluationMutationMap.delete(evaluationVersionId)
  }
}

function addOrRemoveIdFromCreateVersionMutationMap(
  id: string,
  operation: 'add' | 'remove'
) {
  if (operation === 'add') {
    createEvaluationVersionMutationMap.set(id, true)
  } else if (operation === 'remove') {
    createEvaluationVersionMutationMap.delete(id)
  }
}

type EvaluationStatusObj = { key: string; value: string }
export type EvaluationStatusType = Array<EvaluationStatusObj> | null | undefined

export function getCurrentEvaluationStatus(
  evaluationStatus: EvaluationStatusType
) {
  const status: {
    statusObject?: EvaluationStatusObj
    index: number
    value?: string
  } = {
    statusObject: undefined,
    index: -1,
    value: undefined,
  }
  if (!evaluationStatus) {
    return status
  }
  const statusIndex = evaluationStatus.findIndex((statusKV) =>
    TargetStatusKeys.includes(statusKV.key.toLowerCase())
  )
  if (statusIndex === -1) {
    return status
  }
  const statusObject = evaluationStatus[statusIndex]

  status.index = statusIndex
  status.statusObject = statusObject
  status.value = statusObject.value

  return status
}

export type MutateEvaluationStatusArgs = {
  evaluationId: string
  evaluationVersionId: string
  newStatus: StatusValue
}
async function mutationFn(args: MutateEvaluationStatusArgs) {
  const response = gqlClient().request(changeEvaluationStatusMutation, args)
  return response
}

function getUpdatedStatus({
  currentStatusData,
  newStatus,
}: {
  currentStatusData?: EvaluationStatusType
  newStatus: StatusValue
}) {
  let evaluationStatus = [...(currentStatusData ?? [])]
  const { index: statusObjectIndex, statusObject } =
    getCurrentEvaluationStatus(evaluationStatus)
  // If the status object is already there update it
  if (statusObject) {
    evaluationStatus[statusObjectIndex] = {
      ...statusObject,
      value: newStatus,
    }
  } else {
    // Else create an object and add to the array
    evaluationStatus.push({
      key: TargetStatusKeys[0],
      value: newStatus,
    })
  }
  return evaluationStatus
}

export type ChangeEvaluationStatusReportMutateFn = UseMutateAsyncFunction<
  ChangeEvaluationStatusMutation,
  unknown,
  MutateEvaluationStatusArgs,
  {
    previousStatusData?: null
    previousEvaluationData: unknown
  }
>

export function useChangeEvaluationStatusReportMutation(
  evaluationVersionId: string
) {
  const router = useRouter()
  const queryClient = useQueryClient()
  const queryKey = useMemo(
    () => [
      'EvaluationVersionClient',
      {
        id: evaluationVersionId,
      },
    ],
    [evaluationVersionId]
  )
  const dashboardQueryKey = useMemo(
    () => ['DashboardEvaluations', undefined],
    []
  )

  return useMutation({
    mutationFn,
    async onMutate(variables) {
      // Add id to relevant mutation map to track loading states
      addOrRemoveIdFromChangeStatusMutationMap(
        variables.newStatus,
        variables.evaluationVersionId,
        'add'
      )
      const previousEvaluationData = queryClient.getQueryData(queryKey)
      queryClient.setQueryData<EvaluationVersionClientQuery>(
        queryKey,
        (old: EvaluationVersionClientQuery) => {
          if (!old) {
            return old
          }
          const evaluation = old.evaluation
          const evaluationStatus = getUpdatedStatus({
            currentStatusData: evaluation.status,
            newStatus: variables.newStatus,
          })
          // Update the data and return
          return set(`evaluation.status`, evaluationStatus, old)
        }
      )

      return {
        previousEvaluationData,
      }
    },
    onError(_err, _res, ctx) {
      // Revert data on error.
      // The old data is being passed in via the ctx and was returned from the onMutate event above
      queryClient.setQueryData(queryKey, ctx?.previousEvaluationData)
    },
    onSuccess(_data, variables) {
      // Update dashboard evaluations in the cache
      queryClient.setQueryData<DashboardEvaluationsQuery>(
        dashboardQueryKey,
        (currentData: DashboardEvaluationsQuery) => {
          const evaluationIndex =
            currentData?.dashboard?.evaluations?.findIndex(
              (evaluation) => evaluation.id === variables.evaluationVersionId
            ) ?? -1
          if (evaluationIndex === -1) {
            return currentData
          }
          const evaluation =
            currentData?.dashboard?.evaluations[evaluationIndex]
          const newStatusData = getUpdatedStatus({
            currentStatusData: evaluation.status,
            newStatus: variables.newStatus,
          })
          return set(
            `dashboard.evaluations[${evaluationIndex}].status`,
            newStatusData,
            currentData
          )
        }
      )
      if (
        [StatusValue.archived, StatusValue.deleted].includes(
          variables.newStatus
        )
      ) {
        router.push(`/`)
      }
    },
    onSettled(_data, _err, variables) {
      addOrRemoveIdFromChangeStatusMutationMap(
        variables.newStatus,
        variables.evaluationVersionId,
        'remove'
      )
    },
  })
}

export type ChangeEvaluationStatusDashboardMutateFn = UseMutateAsyncFunction<
  ChangeEvaluationStatusMutation,
  unknown,
  MutateEvaluationStatusArgs,
  {
    previousStatusData: null
    evaluationIndex: number
  }
>

export function useChangeEvaluationStatusDashboardMutation() {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['DashboardEvaluations', undefined], [])
  return useMutation({
    mutationFn,
    async onMutate(variables) {
      // Add id to relevant mutation map to track loading states
      addOrRemoveIdFromChangeStatusMutationMap(
        variables.newStatus,
        variables.evaluationVersionId,
        'add'
      )
      let previousStatusData: EvaluationStatusType = null
      let evaluationIndex = -1
      queryClient.setQueryData<DashboardEvaluationsQuery>(
        queryKey,
        (
          old: DashboardEvaluationsQuery = {
            __typename: 'Query',
            dashboard: { evaluations: [] },
          }
        ) => {
          evaluationIndex = old.dashboard.evaluations.findIndex(
            (evaluation) => evaluation.id === variables.evaluationVersionId
          )
          if (evaluationIndex === -1) {
            return old
          }
          const evaluation = old.dashboard.evaluations[evaluationIndex]
          previousStatusData = evaluation.status
          const evaluationStatus = getUpdatedStatus({
            currentStatusData: previousStatusData,
            newStatus: variables.newStatus,
          })

          // Update the data and return
          return set(
            `dashboard.evaluations[${evaluationIndex}].status`,
            evaluationStatus,
            old
          )
        }
      )

      return {
        previousStatusData,
        evaluationIndex,
      }
    },
    onError(_err, _res, ctx) {
      // Revert data on error.
      // The old data is being passed in via the ctx and was returned from the onMutate event above
      if (!ctx?.previousStatusData || ctx.evaluationIndex === -1) {
        return
      }

      queryClient.setQueryData<WorkspaceDashboardQuery>(
        queryKey,
        (oldData: WorkspaceDashboardQuery) => {
          return set(
            `dashboard.evaluations[${ctx.evaluationIndex}].status`,
            ctx?.previousStatusData,
            oldData
          )
        }
      )
    },
    onSettled(_data, _err, variables) {
      addOrRemoveIdFromChangeStatusMutationMap(
        variables.newStatus,
        variables.evaluationVersionId,
        'remove'
      )
    },
  })
}

const createEvaluation_EvaluationFragment = graphql(`
  fragment CreateEvaluation_Evaluation on Evaluation {
    id
    name
    slug
    modDate
    createDate
    versionNumber
    type
    owner {
      auth0Id
      name
      memberId
      profile {
        avatar
      }
    }
    collaborators {
      id
      avatar
      name
      email
      roles
      allowPermissions
    }
    status {
      key
      value
    }
    evaluationVersions {
      id
      evaluationId
      versionNumber
    }
    workspace {
      id
      creatorId
    }
  }
`)

const createEvaluationVersionMutation = graphql(`
  mutation CreateEvaluationVersionMutation($input: EvaluationVersionInput!) {
    createEvaluationVersion(input: $input) {
      evaluation {
        ...CreateEvaluation_Evaluation
      }
      sectionName
    }
  }
`)

export type CreateEvaluationVersionMutateFn = UseMutateAsyncFunction<
  any,
  unknown,
  EvaluationVersionInput,
  unknown
>

export const useCreateEvaluationVersionMutation = () => {
  const addEvaluationToDashboardList = useAddEvaluationToDashboardList()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: EvaluationVersionInput) =>
      gqlClient().request(createEvaluationVersionMutation, { input }),
    async onMutate(variables) {
      // Add id to relevant mutation map to track loading states
      addOrRemoveIdFromCreateVersionMutationMap(
        variables.evaluationId ?? variables?.evaluationVersionId ?? '',
        'add'
      )
    },
    onSuccess(data) {
      const newEvaluation = getFragmentData(
        createEvaluation_EvaluationFragment,
        data.createEvaluationVersion.evaluation
      )
      // Add newly created evaluation to dashboard list
      addEvaluationToDashboardList(newEvaluation)

      // The following is probably not required anymore since we are removing the old evaluations
      // with the same parent evaluation id now. But keeping this as a sanity check
      // and for future if we have to do it again since it took a bit to figure out

      // Filter out the EvaluationVersion queries with the evaluation id matching this version
      // and add this version to their versions
      const queryCache = queryClient.getQueryCache()
      queryCache
        .getAll()
        .filter(
          (cache) =>
            cache.queryKey[0] === 'EvaluationVersionClient' &&
            cache.queryKey[1] !== newEvaluation.id &&
            (cache.state.data as EvaluationVersionClientQuery).evaluation
              .evaluationVersions?.[0]?.evaluationId ===
              newEvaluation.evaluationVersions?.[0]?.evaluationId
        )
        .forEach((cache) => {
          queryClient.setQueryData<EvaluationVersionClientQuery>(
            cache.queryKey,
            (currentData: EvaluationVersionClientQuery) => {
              return set(
                `evaluation.evaluationVersions`,
                newEvaluation.evaluationVersions,
                currentData
              )
            }
          )
        })
      // Also do the same as above for dashboard evaluations
      queryClient.setQueryData<DashboardEvaluationsQuery>(
        ['DashboardEvaluations', undefined],
        (currentData: DashboardEvaluationsQuery) => {
          if (!currentData) return
          const updatedEvaluations = [...currentData.dashboard.evaluations]
          updatedEvaluations.forEach((evaluation, evaluationIndex) => {
            if (
              evaluation.id !== newEvaluation.id &&
              evaluation.evaluationVersions?.[0]?.evaluationId ===
                newEvaluation.evaluationVersions?.[0]?.evaluationId
            ) {
              updatedEvaluations[evaluationIndex] = set(
                'evaluationVersions',
                newEvaluation.evaluationVersions,
                evaluation
              )
            }
          })

          return set(`dashboard.evaluations`, updatedEvaluations, currentData)
        }
      )
    },
    onSettled(_data, _err, variables) {
      // Remove id from the relevant mutation map to track loading states
      addOrRemoveIdFromCreateVersionMutationMap(
        variables.evaluationId ?? variables?.evaluationVersionId ?? '',
        'remove'
      )
    },
  })
}
